// src/components/friendship/FriendshipModulesIntro.jsx
import React from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, ArrowRight, Users, Brain, Heart, Shield,
  Zap, Target, Clock, Sparkles, Wrench,
  CheckCircle, TrendingUp, Calendar, Settings
} from 'lucide-react';
import Button from '../shared/Button';
import Card from '../shared/Card';
import { useApp } from '../../context/AppContext';

const FriendshipModulesIntro = () => {
  const { setCurrentView } = useApp();

  // Complete friendship modules data with beautiful images
  const friendshipModules = [
    {
      id: 1,
      title: 'The Social Brain',
      subtitle: 'How Your Mind Creates Friendships',
      description: 'Discover the neuroscience behind friendship formation and why some connections feel effortless while others feel forced.',
      level: 'Foundation',
      duration: '15 minutes',
      coolFactor: 'Your brain on friendship',
      image: '/images/friendship-01-social-brain.png',
      unlocked: true,
    },
    {
      id: 2,
      title: 'Friendship Roles & Dynamics',
      subtitle: 'The Cast of Characters in Your Social Circle',
      description: 'Learn to identify the different roles friends play in your life and how to navigate complex group dynamics.',
      level: 'Foundation',
      duration: '18 minutes',
      coolFactor: 'Like understanding your friendship ecosystem',
      image: '/images/friendship-02-roles.png',
      unlocked: true,
    },
    {
      id: 3,
      title: 'Conflict Navigation',
      subtitle: 'From Drama to Resolution',
      description: 'Master the art of handling disagreements without losing friendships. Turn conflicts into opportunities for deeper connection.',
      level: 'Essential',
      duration: '22 minutes',
      coolFactor: 'Conflict resolution like a pro mediator',
      image: '/images/friendship-03-conflict.png',
      unlocked: true,
    },
    {
      id: 4,
      title: 'Digital Friendship Drama',
      subtitle: 'Social Media & Modern Connection',
      description: 'Navigate the complex world of online friendships, social media etiquette, and maintaining real connections in a digital age.',
      level: 'Essential',
      duration: '20 minutes',
      coolFactor: 'Digital age relationship wisdom',
      image: '/images/friendship-04-digital-drama.png',
      unlocked: true,
    },
    {
      id: 5,
      title: 'Friendship Endings',
      subtitle: 'When Relationships Change or End',
      description: 'Learn how to handle friendship breakups, drifting apart, and major life transitions that affect your social circle.',
      level: 'Advanced',
      duration: '25 minutes',
      coolFactor: 'Graceful endings and new beginnings',
      image: '/images/friendship-05-endings.png',
      unlocked: true,
    },
    {
      id: 6,
      title: 'Finding Your People',
      subtitle: 'Building Your Chosen Family',
      description: 'Discover how to attract and cultivate the friendships that truly align with who you are and who you\'re becoming.',
      level: 'Advanced',
      duration: '28 minutes',
      coolFactor: 'Creating your dream friend circle',
      image: '/images/friendship-06-your-people.png',
      unlocked: true,
    },
    {
      id: 7,
      title: 'Social Confidence Mastery',
      subtitle: 'Authentic Charisma & Connection',
      description: 'Build genuine social confidence that attracts the right people and helps you show up authentically in any social situation.',
      level: 'Mastery',
      duration: '30 minutes',
      coolFactor: 'Magnetic authenticity',
      image: '/images/friendship-07-social-confidence.png',
      unlocked: false,
    },
    {
      id: 8,
      title: 'Friendship Architect',
      subtitle: 'Designing Your Social Life',
      description: 'Put it all together to intentionally design and maintain a friendship ecosystem that supports your growth and happiness.',
      level: 'Mastery',
      duration: '35 minutes',
      coolFactor: 'Relationship architect mastery',
      image: '/images/friendship-08-mastery.png',
      unlocked: false,
    },
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Foundation': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'Essential': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'Advanced': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'Mastery': return 'bg-gold-500/20 text-gold-300 border-gold-500/30';
      default: return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const stats = [
    { icon: BookOpen, label: '8 Modules', value: 'Complete Journey' },
    { icon: Clock, label: '3+ Hours', value: 'Deep Learning' },
    { icon: Wrench, label: '8 Tools', value: 'One per module' },
    { icon: Users, label: 'Science-Based', value: 'Research Backed' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 pt-32">
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="text-6xl mb-4">🚀</div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Transform Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-coral-400 to-gold-400">
              Friendship Skills
            </span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Master the art and science of meaningful relationships with our comprehensive 8-module journey.
            From understanding how your brain forms connections to designing your ideal social circle.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white/5 rounded-lg p-4 border border-white/10"
              >
                <stat.icon className="w-6 h-6 text-teal-400 mx-auto mb-2" />
                <div className="text-sm text-slate-300">{stat.label}</div>
                <div className="text-lg font-bold text-white">{stat.value}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Learning Path Preview */}
        <div className="max-w-6xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">Your Friendship Mastery Journey</h2>
            <p className="text-lg text-slate-300">
              Each module builds on the last, taking you from friendship novice to relationship architect
            </p>
          </motion.div>

          {/* Modules Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {friendshipModules.map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <Card className={`h-full hover-lift ${!module.unlocked ? 'opacity-75' : ''}`}>
                  {/* Module Image */}
                  <div className="relative">
                    <img
                      src={module.image}
                      alt={module.title}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />

                    {/* Level Badge */}
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full border text-sm font-medium ${getLevelColor(module.level)}`}>
                      {module.level}
                    </div>

                    {/* Lock Overlay for locked modules */}
                    {!module.unlocked && (
                      <div className="absolute inset-0 bg-slate-900/50 rounded-t-lg flex items-center justify-center">
                        <div className="text-white text-center">
                          <Shield className="w-8 h-8 mx-auto mb-2 opacity-80" />
                          <div className="text-sm font-medium">
                            {module.id === 7 ? 'Complete 2 modules to unlock' :
                             module.id === 8 ? 'Complete 4 modules to unlock' :
                             `Unlocks after Module ${module.id - 1}`}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-white">{module.title}</h3>
                      <div className="text-2xl font-bold text-teal-400">{module.id}</div>
                    </div>

                    <p className="text-yellow-300 font-medium mb-2">{module.subtitle}</p>
                    <p className="text-slate-200 text-sm mb-4">{module.description}</p>

                    {/* Meta Info */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-300 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {module.duration}
                        </span>
                      </div>

                      <div className="text-xs text-purple-300 italic">
                        💡 {module.coolFactor}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Three Pillars Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="max-w-6xl mx-auto mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">Your Complete Friendship Transformation System</h3>
            <p className="text-lg text-slate-300">Three powerful tools working together to transform your relationships</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Assessment Pillar */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
            >
              <Card className="p-8 h-full bg-gradient-to-b from-purple-500/10 to-indigo-500/10 border-purple-500/30 hover-lift">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Brain className="w-8 h-8 text-purple-400" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">1. Assessment</h4>
                  <p className="text-purple-300 font-medium">Discover Your Friendship DNA</p>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-blue-200 text-sm">Identify your friendship archetype</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-blue-200 text-sm">Understand your relationship patterns</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-blue-200 text-sm">Get personalised insights & recommendations</span>
                  </div>
                </div>

                <div className="bg-slate-800/50 p-3 rounded-lg mb-4">
                  <div className="text-xs text-slate-300 mb-1">WHAT YOU'LL GET:</div>
                  <p className="text-sm text-white">Complete friendship profile with strengths, growth areas, and custom learning path</p>
                </div>

                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => setCurrentView('vibe-assessment-intro')}
                >
                  Take Assessment
                </Button>
              </Card>
            </motion.div>

            {/* Dive In Pillar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <Card className="p-8 h-full bg-gradient-to-b from-teal-500/10 to-coral-500/10 border-teal-500/30 hover-lift">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-teal-400" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">2. Dive In</h4>
                  <p className="text-teal-300 font-medium">Master Friendship Skills</p>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-white text-sm">8 comprehensive learning modules</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-white text-sm">Science-backed strategies & techniques</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-white text-sm">Progressive skill-building journey</span>
                  </div>
                </div>

                <div className="bg-slate-800/50 p-3 rounded-lg mb-4">
                  <div className="text-xs text-slate-300 mb-1">WHAT YOU'LL LEARN:</div>
                  <p className="text-sm text-white">From social neuroscience to conflict resolution to designing your ideal friend circle</p>
                </div>

                <Button
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  onClick={() => setCurrentView('modules')}
                >
                  Start Learning
                </Button>
              </Card>
            </motion.div>

            {/* Your Toolkit Pillar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1 }}
            >
              <Card className="p-8 h-full bg-gradient-to-b from-coral-500/10 to-gold-500/10 border-coral-500/30 hover-lift">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wrench className="w-8 h-8 text-orange-300" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">3. Your Toolkit</h4>
                  <p className="text-orange-200 font-medium">Practise & Apply</p>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-orange-200 text-sm">Interactive exercises & simulations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-orange-200 text-sm">Real-world scenario practice</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-orange-200 text-sm">Personalised reflection tools</span>
                  </div>
                </div>

                <div className="bg-slate-800/50 p-3 rounded-lg mb-4">
                  <div className="text-xs text-slate-300 mb-1">WHAT YOU'LL DO:</div>
                  <p className="text-sm text-white">Practice conflict resolution, conversation starters, boundary setting & more</p>
                </div>

                <Button
                  className="w-full bg-coral-600 hover:bg-coral-700"
                  onClick={() => setCurrentView('tools')}
                >
                  Explore Tools
                </Button>
              </Card>
            </motion.div>
          </div>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <Card className="p-8 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border-slate-600/30">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              Why This Works ✨
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-6 h-6 text-teal-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Progressive Learning</h4>
                    <p className="text-blue-200 text-sm">Each module builds on the last, creating lasting transformation</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Target className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Practical Application</h4>
                    <p className="text-white text-sm">Every lesson includes tools and exercises you can use immediately</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Heart className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Personalised Journey</h4>
                    <p className="text-orange-200 text-sm">Tailored content based on your assessment and learning style</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Pick Up Where You Left Off</h4>
                    <p className="text-teal-200 text-sm">Your dashboard keeps your place in each module, and one thing from it to try each week</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="text-center"
        >
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Friendships?
          </h3>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Start with the assessment to discover your friendship style, then dive into personalised modules and practice with interactive tools.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-xl"
              onClick={() => setCurrentView('vibe-assessment-intro')}
            >
              <Brain className="w-5 h-5 mr-2" />
              Start with Assessment
            </Button>

            <Button
              size="lg"
              variant="secondary"
              className="border-teal-500/50 hover:bg-teal-500/10"
              onClick={() => setCurrentView('modules')}
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Jump to Modules
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          <div className="bg-slate-800/30 rounded-lg p-4 max-w-2xl mx-auto">
            <div className="text-sm text-slate-300 mb-2">
              <strong className="text-white">Recommended path:</strong>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-300">
              <span className="flex items-center gap-1">
                <Brain className="w-4 h-4 text-purple-400" />
                Assessment
              </span>
              <ArrowRight className="w-3 h-3" />
              <span className="flex items-center gap-1">
                <BookOpen className="w-4 h-4 text-teal-400" />
                Modules
              </span>
              <ArrowRight className="w-3 h-3" />
              <span className="flex items-center gap-1">
                <Wrench className="w-4 h-4 text-coral-400" />
                Practice Tools
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FriendshipModulesIntro;